import React from 'react';
import { Check, TrendingUp } from 'lucide-react';

const bullets = [
  'We understand performance marketing and conversion strategy',
  'We work across paid media, analytics, creative, and funnel optimization',
  'We focus on user and revenue quality, not vanity metrics',
  'We can support both early-stage app companies and established Norwegian brands',
  'We communicate clearly and work as an extension of your team',
];

const metrics = [
  { label: 'CAC', value: 'NOK 12.40', trend: '-32%', good: true },
  { label: 'Trial start rate', value: '11.8%', trend: '+2.4pp', good: true },
  { label: 'Paid conversion', value: '4.1%', trend: '+1.1pp', good: true },
  { label: 'ROAS', value: '5.8×', trend: '+1.4×', good: true },
  { label: 'Retention D30', value: '38.2%', trend: '+6pp', good: true },
  { label: 'ARPU', value: 'NOK 82', trend: '+22%', good: true },
];

const Why = () => {
  return (
    <section
      data-testid="app-growth-why"
      className="py-24 px-4 sm:px-6 lg:px-8 bg-gray-50"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          {/* Left: copy */}
          <div>
            <div className="inline-flex items-center gap-2 text-blue-600 text-sm font-semibold uppercase tracking-wider mb-4">
              Why Upscale
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight tracking-tight">
              For app teams that need{' '}
              <span className="text-blue-600">senior growth thinking</span> and hands-on execution.
            </h2>
            <p className="mt-6 text-lg text-gray-600 leading-relaxed">
              App growth is different from ordinary lead generation or ecommerce advertising. You
              need to understand store conversion, mobile attribution, subscription economics,
              creative fatigue, platform learning, and the difference between cheap installs and
              valuable users.
            </p>
            <p className="mt-4 text-base text-gray-600 leading-relaxed">
              Upscale helps bridge strategy and execution — so growth work becomes more focused,
              measurable, and commercially useful.
            </p>

            <ul className="mt-8 space-y-4">
              {bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex-shrink-0 flex items-center justify-center mt-0.5">
                    <Check size={14} strokeWidth={3} />
                  </div>
                  <span className="text-gray-700 leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right: dashboard */}
          <div className="relative">
            <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 p-8 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-40 h-40 bg-blue-500/5 rounded-full blur-3xl" />
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold">
                    Growth Dashboard
                  </div>
                  <div className="text-lg font-bold text-gray-900 mt-1">Last 30 days</div>
                </div>
                <div className="flex items-center gap-1.5 text-blue-600 text-sm font-semibold">
                  <TrendingUp size={16} /> All positive
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {metrics.map((m, i) => (
                  <div
                    key={i}
                    className="bg-gradient-to-br from-gray-50 to-white border border-gray-100 rounded-xl p-4"
                  >
                    <div className="text-xs text-gray-500">{m.label}</div>
                    <div className="text-xl font-bold text-gray-900 mt-1">{m.value}</div>
                    <div
                      className={`text-xs mt-1 font-semibold ${
                        m.good ? 'text-blue-600' : 'text-red-500'
                      }`}
                    >
                      {m.trend}
                    </div>
                  </div>
                ))}
              </div>

              {/* Sparkline */}
              <div className="mt-6 pt-6 border-t border-gray-100">
                <div className="flex items-end gap-1 h-16">
                  {[40, 55, 48, 65, 58, 72, 68, 82, 78, 90, 85, 96].map((h, i) => (
                    <div
                      key={i}
                      className="flex-1 bg-gradient-to-t from-blue-500 to-blue-400 rounded-sm"
                      style={{ height: `${h}%` }}
                    />
                  ))}
                </div>
                <div className="flex justify-between text-[10px] text-gray-400 mt-2">
                  <span>Jan</span>
                  <span>Growth compounding →</span>
                  <span>Dec</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Why;
